import React, { useState } from "react";
import styles from "./ForecastSection.module.css";
import Button from "../../../components/common/Button";
import Preloader from "../../../components/common/Preloader";
import StepError from "../../../components/common/StepError";
import { fetchYearlyForecast } from "../../../services/api/forecast";
import { useForecastStore } from "../../../store/forecastStore";
import { useSubscriptionForecast } from "../../../hooks/useSubscriptionForecast";
import { useProfileStore } from "../../../store/profileStore";
import { useSubscriptionPayment } from "../../../hooks/useSubscriptionPayment";
import { useShareForecast } from "../../../hooks/useShareForecast"; // импорт

const MAX_PREVIEW_LENGTH = 600;

const ForecastYear: React.FC = () => {
  const { yearlyCache, setYearlyForecast } = useForecastStore();
  const currentYear = new Date().getFullYear();
  const [isFullVisible, setIsFullVisible] = useState(false);

  const userId = useProfileStore((s) => s.profile?.user_id ?? null);

  const { forecast, subscriptionMessage, loading, error, reload } = useSubscriptionForecast({
    getForecast: async () => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const data = await fetchYearlyForecast(timezone);
      return data.forecast ?? "";
    },
    cacheValue: yearlyCache?.year === currentYear ? yearlyCache.forecast : null,
    setCache: (val) => setYearlyForecast(currentYear, val),
  });

  const { paying, payError, handlePay } = useSubscriptionPayment(userId, reload);

  // --- useShareForecast ---
  const { showCopyModal, setShowCopyModal, toast, textAreaRef, shareForecast, manualCopy } =
    useShareForecast();

  const shareText = `Прогноз на год:\n\n${forecast}`;

  if (!userId) {
    return (
      <StepError
        message="Не удалось определить пользователя. Перезапустите приложение."
        onRetry={reload}
        showRetry
      />
    );
  }

  if (loading)
    return (
      <div style={{ marginTop: "-150px" }}>
        <Preloader width={220} height={220} />
      </div>
    );

  if (error || payError)
    return <StepError message={error || payError || ""} onRetry={reload} showRetry />;

  if (subscriptionMessage)
    return (
      <div className={styles.section}>
        <h3 className={styles.forecastTitle}>Прогноз на год</h3>
        <p className={styles.forecastText}>{subscriptionMessage}</p>
        <div className={styles.cta}>
          <Button onClick={handlePay} disabled={paying}>
            {paying ? "Ожидание..." : "Подписаться"}
          </Button>
        </div>
      </div>
    );

  const isLongForecast = (forecast?.length ?? 0) > MAX_PREVIEW_LENGTH;

  return (
    <>
      <div className={styles.section}>
        <div className={styles.titleBlock}>
          <h3 className={styles.forecastTitle}>Прогноз на год</h3>
          <a
            href="#"
            className={styles.share}
            onClick={(e) => {
              e.preventDefault();
              shareForecast(shareText);
            }}
          >
            <svg width={28} height={28} viewBox="0 0 28 28" fill="none">
              <rect x="5" y="12" width="18" height="10" rx="4" stroke="#fff" strokeWidth="1.2" />
              <path d="M14 18V5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
              <path
                d="M10 9l4-4 4 4"
                stroke="#fff"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
        <p className={styles.forecastText}>
          {isFullVisible || !isLongForecast
            ? forecast
            : forecast?.slice(0, MAX_PREVIEW_LENGTH) + "..."}
        </p>
        {isLongForecast && (
          <div className={styles.toggleButtonWrapper}>
            <Button onClick={() => setIsFullVisible(!isFullVisible)}>
              {isFullVisible ? "Скрыть прогноз" : "Показать полностью"}
            </Button>
          </div>
        )}
      </div>
      {toast && <div className={styles.toast}>{toast}</div>}
      {showCopyModal && (
        <div className={styles.copyModal}>
          <div className={styles.copyModalContent}>
            <textarea
              ref={textAreaRef}
              value={shareText}
              readOnly
              style={{ width: "90%", minHeight: 120, fontSize: 14 }}
              onFocus={(e) => e.target.select()}
            />
            <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 12 }}>
              <Button onClick={manualCopy}>Копировать</Button>
              <Button onClick={() => setShowCopyModal(false)}>Закрыть</Button>
            </div>
            <div className={styles.copyHint}>
              Если копирование не сработало, выделите текст вручную.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ForecastYear;
